import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { startWith, map, takeUntil, finalize } from 'rxjs/operators';
import { AlertService } from '@services/alert.service';
import { Markets } from '@models/markets';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/state/app.state';
import { GetAlert } from '@store/actions/user.action';
import { getMarkets } from '@store/selectors/market.selector';
import { MessageService } from '@services/message.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-novo-alerta',
    templateUrl: './novo-alerta.component.html',
    styleUrls: ['./novo-alerta.component.scss'],
    standalone: false
})
export class NovoAlertaComponent implements OnInit, OnDestroy {

  newAlertForm: UntypedFormGroup;
  numberOnly = '^[0-9]{1,6}([,.][0-9]{1,2})?$';

  isNavBarOpened = false;
  // True while the request is running: avoids creating the alert more than once
  pending = false;

  markets: Markets[] = [];

  filteredOptions: Observable<Markets[]>;

  private unsubscribe$: Subject<void> = new Subject();

  constructor(private formBuilder: UntypedFormBuilder,
              private alertService: AlertService,
              private messageService: MessageService,
              private router: Router,
              public store: Store<AppState>,
              public translateService: TranslateService) {

    this.store.pipe(
      select(getMarkets),
      takeUntil(this.unsubscribe$)
    ).subscribe(markets => {
      if (markets) {
        this.markets = markets;
      }
    });
  }

  ngOnInit() {
    this.newAlertForm = this.formBuilder.group({
      product: ['', Validators.required],
      variation: ['', Validators.required],
      price: ['', [Validators.required, Validators.pattern(this.numberOnly)]]
    });

    this.filteredOptions = this.newAlertForm.controls.product.valueChanges
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.ns),
        map(name => name ? this._filter(name) : this.markets.slice())
      );
  }

  displayFn(market: Markets): string {
    return market && market.ns ? market.ns.split('_').join(' ') : '';
  }

  private _filter(name: string): Markets[] {
    const filterValue = name.toLowerCase();

    return this.markets.filter(option => option.ns.toLowerCase().includes(filterValue));
  }

  get f() { return this.newAlertForm.controls; }

  onSubmit() {
    if (this.newAlertForm.invalid || this.pending) {
        return;
    }

    const product = this.newAlertForm.value.product.ns;
    const price = String(this.newAlertForm.value.price).replace(/,/g, '.');
    const variation = this.newAlertForm.value.variation;

    const args = { mns: product, vid: variation, val: price };

    this.pending = true;
    this.alertService.createAlert(args).pipe(
      finalize(() => this.pending = false)
    ).subscribe((response) => {
      if (response.status === 200 && response.success) {
        this.store.dispatch(new GetAlert());
        this.messageService.show(this.translateService.instant('edp.emarket.editar.alertas.definido.sucesso'));
        this.router.navigate(['/manageAlerts']);
      } else {
        this.messageService.show(this.translateService.instant('edp.emarket.editar.alertas.definido.error'), 'error');
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
